import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { ErrorModal } from "../ModalAlert";
import { getCurrentEmployeeId } from "../../utils/currentEmployee";
import supabase from "../../supabase";
import OrdersTable from "../ordersTable";
import OperatorFilter from "../ordersTable/OperatorFilter";
import { LoaderSpinner } from "../spinner/loader";
import {
  operatorHiddenOrderStatuses,
  selectableOrderStatus,
} from "../../constants/orderStatus";
import { getOrderStatusStyle } from "../../constants/statusStyles";
import { useIsMobile } from "../../hooks/useIsMobile";
import {
  applyDirectFilters,
  applyOrdersSearch,
  buildOrdersSearchOrFilter,
  getOrdersSelect,
  getSortAscending,
  getSortColumn,
  mapOrderRow,
} from "../../utils/ordersListQuery";

const DEFAULT_ROWS_PER_PAGE = 15;
const MOBILE_ROWS_PER_PAGE = 10;

const OrdersScreen = ({ isAdmin = false }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [sortOrder, setSortOrder] = useState({ name: "orderId", direction: "desc" });
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [employeeFilter, setEmployeeFilter] = useState(null);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const isMobile = useIsMobile(768);

  const tableParamsRef = useRef({
    page: 0,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
    sortOrder: { name: "orderId", direction: "desc" },
  });

  const rowsPerPage = isMobile ? MOBILE_ROWS_PER_PAGE : DEFAULT_ROWS_PER_PAGE;

  // El operador sólo ve órdenes pendientes: no tiene sentido ofrecerle los demás chips.
  const visibleStatuses = isAdmin
    ? selectableOrderStatus
    : selectableOrderStatus.filter((s) => !operatorHiddenOrderStatuses.includes(s.value));

  useEffect(() => {
    tableParamsRef.current.rowsPerPage = rowsPerPage;
  }, [rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchEmployees = useCallback(async () => {
    const { data, error } = await supabase
      .from("employees")
      .select("employeeId, name, lastName")
      .order("name", { ascending: true });

    if (error) {
      console.error("Error cargando operadores:", error);
      return;
    }
    setEmployees(data || []);
  }, []);

  const fetchOrders = useCallback(
    async (params) => {
      const {
        page = 0,
        rowsPerPage: limit = rowsPerPage,
        sortOrder: nextSortOrder = { name: "orderId", direction: "desc" },
        searchText: search = "",
        statusFilter: status = null,
        employeeFilter: employeeId = null,
        dateFrom = null,
        dateTo = null,
      } = params;

      setLoading(true);
      setSortOrder(nextSortOrder);
      try {
        let query = supabase.from("orders").select(getOrdersSelect(), { count: "exact" });

        if (!isAdmin) {
          query = query.not("status", "in", `(${operatorHiddenOrderStatuses.join(",")})`);

          // El operador sólo ve las órdenes que cargó él.
          const propio = await getCurrentEmployeeId();
          if (propio === null) {
            // Sin poder resolver quién es, no se muestra nada: es preferible a
            // mostrarle las órdenes de todos.
            throw new Error("No se pudo identificar al operador de la sesión");
          }
          query = query.eq("employeeId", propio);
        }

        const searchOrFilter = await buildOrdersSearchOrFilter(supabase, search);
        query = applyOrdersSearch(query, searchOrFilter);
        query = applyDirectFilters(query, { status, employeeId });
        if (dateFrom) query = query.gte("createdAt", new Date(dateFrom).toISOString());
        if (dateTo) query = query.lte("createdAt", new Date(dateTo).toISOString());

        query = query.order(getSortColumn(nextSortOrder), {
          ascending: getSortAscending(nextSortOrder),
        });

        const from = page * limit;
        query = query.range(from, from + limit - 1);

        const { data: ordersData, error, count } = await query;
        if (error) throw new Error(error.message);

        setOrders((ordersData || []).map(mapOrderRow));
        setTotalCount(count ?? 0);
      } catch (error) {
        ErrorModal({ title: error.message });
        setOrders([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    },
    [isAdmin, rowsPerPage]
  );

  const loadTable = useCallback(
    (overrides = {}) => {
      const next = { ...tableParamsRef.current, ...overrides };
      tableParamsRef.current = next;
      return fetchOrders(next);
    },
    [fetchOrders]
  );

  useEffect(() => {
    if (isAdmin) fetchEmployees();
  }, [fetchEmployees, isAdmin]);

  // Búsqueda y filtros vuelven siempre a la primera página.
  useEffect(() => {
    loadTable({
      page: 0,
      rowsPerPage,
      searchText,
      statusFilter,
      employeeFilter,
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, rowsPerPage, searchText, statusFilter, employeeFilter, dateRange, loadTable]);

  const handleTableChange = useCallback(
    (action, tableState) => {
      if (action === "changePage" || action === "changeRowsPerPage" || action === "sort") {
        loadTable({
          page: action === "changeRowsPerPage" ? 0 : tableState.page,
          rowsPerPage: tableState.rowsPerPage,
          sortOrder: tableState.sortOrder || { name: "orderId", direction: "desc" },
        });
      }
    },
    [loadTable]
  );

  const handleMobileSortToggle = useCallback(() => {
    const current = tableParamsRef.current.sortOrder;
    loadTable({
      sortOrder: {
        name: "orderId",
        direction: current.direction === "desc" ? "asc" : "desc",
      },
      page: 0,
    });
  }, [loadTable]);

  const statusChips = [
    {
      id: "todas",
      label: "Todas",
      active: statusFilter === null,
      onClick: () => setStatusFilter(null),
    },
    ...visibleStatuses.map((status) => ({
      id: status.value,
      label: status.label,
      dot: getOrderStatusStyle(status.value).dot,
      active: statusFilter === status.value,
      onClick: () => setStatusFilter(statusFilter === status.value ? null : status.value),
    })),
  ];

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {!hasLoadedOnce && loading ? (
        <InitialLoadSpinner />
      ) : (
        <OrdersTable
          orders={orders}
          isAdmin={isAdmin}
          loading={loading}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          sortOrder={sortOrder}
          onTableChange={handleTableChange}
          onMobileSortToggle={handleMobileSortToggle}
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: "Buscar por N° de orden, cliente o CUIT…",
          }}
          dateRange={{
            from: dateRange.from,
            to: dateRange.to,
            onChange: (from, to) => setDateRange({ from, to }),
          }}
          chips={statusChips}
          filters={
            // Sin sentido para el operador: sólo ve las suyas.
            isAdmin ? (
              <OperatorFilter
                employees={employees}
                value={employeeFilter}
                onChange={setEmployeeFilter}
              />
            ) : null
          }
        />
      )}
    </Box>
  );
};

function InitialLoadSpinner() {
  return (
    <Box
      sx={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </Box>
  );
}

export default OrdersScreen;
